import { Fragment, useEffect, useState } from "react";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { Alert, Box, Button, ButtonBase, CircularProgress, IconButton, Tooltip, MenuItem, TextField, Typography } from "@mui/material";
import { ArrowDownward, ArrowForward, ArrowUpward, LocationOnOutlined, PersonOutline, SwapHoriz, Undo } from "@mui/icons-material";
import dayjs from "dayjs";
import TransferFeedApi, { TransferEntry } from "@/api/TransferFeedApi";
import { usePersistantStore } from "@/stores/global";
import useRights from "@/shared/api/useRights";
import { useDeviceInfoModal } from "@/components/device/DeviceInfoModal";
import TransferFeedDialog from "./TransferFeedDialog";

export default function BauhofTransfers() {
  const region = usePersistantStore((store) => store.region);
  const user = usePersistantStore((store) => store.userId);
  const placeId = usePersistantStore((store) => store.transferPlace);
  const setPlace = usePersistantStore((store) => store.setTransferPlace);
  const allowed = "DISPOSITION" in useRights();
  const device = useDeviceInfoModal();
  const [action, setAction] = useState<{ entry: TransferEntry; type: "return" | "forward" } | null>(null);
  const places = useQuery({
    queryKey: ["transferPlaces", user, region],
    queryFn: () => TransferFeedApi.places(region),
    enabled: allowed,
  });
  const place = places.data?.find((candidate) => candidate.id === placeId);
  useEffect(() => {
    if (places.isSuccess && placeId && !place) setPlace(0);
  }, [places.isSuccess, place, placeId, setPlace]);
  useEffect(() => { setAction(null); }, [user, region, placeId]);
  const feed = useInfiniteQuery({
    queryKey: ["transferFeed", user, region, place?.id],
    queryFn: ({ pageParam }) => TransferFeedApi.feed(region, place!.id, pageParam),
    initialPageParam: null as number | null,
    getNextPageParam: (page) => page.next ?? undefined,
    enabled: allowed && !!place,
  });
  const entries = feed.data?.pages.flatMap((page) => page.items) ?? [];
  if (!allowed) return null;
  return (
    <Box sx={{ display: "flex", flex: 1, overflow: "auto", flexDirection: "column" }}>
      <Box sx={{ p: 1, borderBottom: "1px solid #aaa", flexShrink: 0 }}>
        <TextField select label="Buchort" variant="standard" fullWidth size="small" value={place?.id ?? 0}
          onChange={(event) => setPlace(Number(event.target.value))} disabled={!places.isSuccess}>
          <MenuItem value={0}>Bitte auswählen</MenuItem>
          {(places.data ?? []).map((candidate) => <MenuItem key={candidate.id} value={candidate.id}>{candidate.name}</MenuItem>)}
        </TextField>
      </Box>
      {places.isPending ? <CircularProgress size={24} /> : null}
      {places.isError ? <Alert severity="error">Die Orte konnten nicht geladen werden. <Button onClick={() => places.refetch()}>Erneut laden</Button></Alert> : null}
      {places.isSuccess && !places.data.length ? <Typography>Keine Orte im ausgewählten Standort.</Typography> : null}
      {place && feed.isPending ? <CircularProgress size={24} /> : null}
      {place && feed.isError ? <Alert severity="error">Der Transferverlauf konnte nicht geladen werden. <Button onClick={() => feed.refetch()}>Erneut laden</Button></Alert> : null}
      {place && feed.isSuccess && !entries.length ? <Typography>Keine erfolgten Transfers für diesen Ort.</Typography> : null}
      {entries.map((entry, index) => (
        <Fragment key={entry.id}>
          {index === 0 || entries[index - 1].date !== entry.date ?
            <Typography sx={{ position: "sticky", top: 0, bgcolor: "#edf0f9", color: "#222", fontWeight: "bold", p: "6px", mb: 1,
              borderTop: "1px solid rgba(0,0,0,0.2)", borderBottom: "1px solid rgba(0,0,0,0.2)",
              boxShadow: "inset 0px -4px 5px rgba(0,0,0,0.1)", fontSize: "14px", lineHeight: 1, flexShrink: 0, zIndex: 1 }}>
              {dayjs(entry.date).format("DD.MM.YYYY")}
            </Typography> : null}
          <Box component="article" sx={{ display: "flex", flexDirection: "column", border: "1px solid #777", borderRadius: "5px",
            overflow: "hidden", mx: "4px", mb: 1, flexShrink: 0, fontSize: "13px" }}>
            <Box sx={{ display: "flex", borderBottom: "1px solid #777" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, p: "4px 6px", borderRight: "1px solid #aaa",
                bgcolor: entry.incoming ? "#dcfbd4" : "#ffe2d6", color: entry.incoming ? "#256029" : "#9b2c16", boxShadow: "inset -4px 0px 5px rgba(0,0,0,0.1)" }}>
                {entry.incoming ? <ArrowDownward fontSize="small" /> : <ArrowUpward fontSize="small" />}
                <Typography variant="caption">{entry.incoming ? "Zugang" : "Abgang"}</Typography>
              </Box>
              <ButtonBase sx={{ flex: 1, minWidth: 0, justifyContent: "flex-start", textAlign: "left", p: "4px 6px",
                "&:hover": { bgcolor: "action.hover" } }} onClick={() => device.showById(entry.resource_id)}>
                <Box component="span" sx={{ overflowWrap: "anywhere" }}>
                  <Box component="span" sx={{ fontSize: "12px", color: "text.secondary", mr: 0.5 }}>{entry.resource_number}</Box>
                  {entry.resource_name}
                </Box>
              </ButtonBase>
            </Box>
            <Box sx={{ bgcolor: "#eee" }}>
              <Box sx={{ p: "4px 6px", borderBottom: "1px solid #aaa", color: "text.secondary", overflowWrap: "anywhere" }}>
                {entry.source_number} {entry.source_name}
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, p: "4px 6px", overflowWrap: "anywhere" }}>
                <ArrowForward sx={{ fontSize: 16, color: "#666", flexShrink: 0 }} />
                <span>{entry.target_number} {entry.target_name}</span>
              </Box>
            </Box>
            {entry.original_erfasser_name || entry.erfasser_name ?
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, p: "4px 6px", borderTop: "1px solid #aaa",
                bgcolor: "#f7f7f7", fontSize: "12px", boxShadow: "0 2px 3px rgba(0,0,0,0.1) inset" }}>
                <PersonOutline sx={{ fontSize: 16, color: "#666", flexShrink: 0 }} />
                <Box sx={{ flex: 1, overflowWrap: "anywhere" }}>
                  {entry.original_erfasser_name ? <div>Von: {entry.original_erfasser_name}</div> : null}
                  {entry.erfasser_name ? <div>An: {entry.erfasser_name}</div> : null}
                </Box>
              </Box> : null}
            {entry.comment ? <Box sx={{ borderTop: "1px solid #aaa", bgcolor: "#f7f7f7", p: "4px 6px",
              fontSize: "12px", whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{entry.comment}</Box> : null}
            {entry.blocked_reason ?
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, borderTop: "1px solid #aaa",
                bgcolor: "#edf0f9", p: "4px 6px", color: "text.secondary", fontSize: "12px" }}>
                <LocationOnOutlined sx={{ fontSize: 16, flexShrink: 0 }} />
                <Box sx={{ overflowWrap: "anywhere" }}>
                  {entry.current_location ? <>Jetzt in {entry.current_location.type === "project" ? "Baustelle " : ""}
                    {entry.current_location.number} {entry.current_location.name}</> : "Aktueller Ort unbekannt"}
                </Box>
              </Box> : entry.incoming || entry.original_project_id || entry.original_special_place_id ?
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, p: "2px 6px", borderTop: "1px solid #aaa" }}>
                {entry.incoming ? <Tooltip title="Weitertransferieren">
                  <IconButton size="small" aria-label="Weitertransferieren" onClick={() => setAction({ entry, type: "forward" })}>
                    <SwapHoriz fontSize="small" />
                  </IconButton>
                </Tooltip> : null}
                {entry.original_project_id || entry.original_special_place_id ?
                  <Tooltip title={entry.incoming ? "Ablehnen / zurücksenden" : "Zurückholen"}>
                    <IconButton size="small" aria-label={entry.incoming ? "Ablehnen / zurücksenden" : "Zurückholen"} onClick={() => setAction({ entry, type: "return" })}>
                      <Undo fontSize="small" />
                    </IconButton>
                  </Tooltip> : null}
              </Box> : null}
          </Box>
        </Fragment>
      ))}
      {feed.hasNextPage ? <Button disabled={feed.isFetchingNextPage} onClick={() => feed.fetchNextPage()}>Ältere Transfers laden</Button> : null}
      {feed.isFetchNextPageError ? <Alert severity="error">Ältere Transfers konnten nicht geladen werden.</Alert> : null}
      {action && place ? <TransferFeedDialog key={`${user}/${region}/${place.id}/${action.entry.id}/${action.type}`}
        entry={action.entry} type={action.type} place={place} region={region} user={user} close={() => setAction(null)} /> : null}
    </Box>
  );
}
